import React, { useState } from 'react';
import * as R from 'ramda';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { arrayOf, bool, shape, string } from 'prop-types';
import { ProductIndicatorBadge } from 'common';

const ProductsAccordion = ({ authorizationType: authType, defaultExpanded, products }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const isProductSpecific = R.equals('SPECIFIC', authType);

  return (
    <Accordion expanded={isExpanded} onChange={() => setExpanded(!isExpanded)}>
      <AccordionSummary aria-controls="products-content" expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1}>
          <Typography variant="body2">Products</Typography>
          {!!products.length && isProductSpecific && (
            <Chip label={products.length} type="accordion" variant="badge" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {products.length ? (
          <>
            <Stack direction="row" sx={{ mb: 1 }}>
              <Typography
                sx={{
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  flex: { xs: 1, lg: 1.5 },
                }}
                variant="caption"
              >
                {lgUp ? 'Product' : 'Product/Auth Type'}
              </Typography>
              {lgUp && (
                <>
                  <Typography sx={{ flex: 0.75 }} variant="caption">
                    Auth Type
                  </Typography>
                  <Typography
                    sx={{
                      flex: 1,
                      textAlign: 'end',
                    }}
                    variant="caption"
                  >
                    Authorization Vol
                  </Typography>
                </>
              )}
              <Typography sx={{ flex: 1, textAlign: 'end' }} variant="caption">
                {lgUp ? 'Renewal Vol' : 'Auth/Renew Vol'}
              </Typography>
              <Typography sx={{ flex: 1, textAlign: 'end' }} variant="caption">
                {lgUp ? 'Remaining Vol' : 'Auth/Remain Vol'}
              </Typography>
            </Stack>
            {products.map(
              ({
                authorizationType,
                authorizationVolume,
                productId,
                productIdName,
                productNameShort,
                isProprietary,
                renewalVolume,
              }) => (
                <Stack direction="row" key={productId} sx={{ mb: 1 }}>
                  {lgUp ? (
                    <Stack direction="row" sx={{ flex: 1.5 }}>
                      <Chip
                        label={productIdName}
                        sx={{ minWidth: 32, px: 0.5, width: 'revert' }}
                        variant="badge"
                      />
                      <Typography
                        noWrap
                        sx={{
                          mx: 0.5,
                          pt: '2px',
                        }}
                        variant="subtitle2"
                      >
                        {productNameShort}
                      </Typography>
                      <ProductIndicatorBadge proprietary={isProprietary} />
                    </Stack>
                  ) : (
                    <Stack sx={{ flex: 1 }}>
                      <Stack direction="row">
                        <Chip
                          label={productIdName}
                          sx={{ minWidth: 32, px: 0.5, width: 'revert' }}
                          variant="badge"
                        />
                        <Typography
                          noWrap
                          sx={{
                            mx: 0.5,
                            pt: '2px',
                          }}
                          variant="subtitle2"
                        >
                          {productNameShort}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <ProductIndicatorBadge proprietary={isProprietary} />
                        <Typography sx={{ ml: 0.5, pt: '2px' }} variant="subtitle2">
                          {authorizationType}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                  {lgUp && (
                    <>
                      <Typography sx={{ flex: 0.75 }} variant="subtitle2">
                        {authorizationType}
                      </Typography>
                      <Typography sx={{ flex: 1, textAlign: 'end' }} variant="subtitle2">
                        {R.defaultTo('--', authorizationVolume)}
                      </Typography>
                    </>
                  )}
                  {lgUp ? (
                    <Typography sx={{ flex: 1, textAlign: 'end' }} variant="subtitle2">
                      {R.defaultTo('--', renewalVolume)}
                    </Typography>
                  ) : (
                    <Stack sx={{ flex: 1 }}>
                      <Typography sx={{ textAlign: 'end' }} variant="subtitle2">
                        {renewalVolume}
                      </Typography>
                      <Typography sx={{ textAlign: 'end' }} variant="subtitle2">
                        {R.defaultTo('--', renewalVolume)}
                      </Typography>
                    </Stack>
                  )}
                  {lgUp && (
                    <>
                      <Typography sx={{ flex: 1, textAlign: 'end' }} variant="subtitle2">
                        {R.defaultTo('--', authorizationVolume - renewalVolume)}
                      </Typography>
                    </>
                  )}
                </Stack>
              )
            )}
          </>
        ) : (
          <Typography>All products are allowed for this authorization.</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

ProductsAccordion.propTypes = {
  authorizationType: string.isRequired,
  defaultExpanded: bool,
  products: arrayOf(shape({})),
};

ProductsAccordion.defaultProps = {
  defaultExpanded: false,
  products: [],
};

export default ProductsAccordion;
